import { useEffect, useRef } from 'react';
import { env } from '@/shared/config';
import { readCookie } from './readCookie';

/**
 * Keeps a session-long WebSocket open to `/ws` for the whole app lifetime.
 *
 * Side effect that matters here: while this connection is open the backend
 * marks the user "online" in the module — that's the `isOnline` flag shown in
 * the "call for help" modal. (The same socket also carries push notifications.)
 *
 * Mounted once at the app root. Reconnects with exponential backoff and waits
 * for the auth cookie before the first attempt.
 */
export function usePresenceSocket() {
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    function connect() {
      if (cancelled) {
        return;
      }
      const token = readCookie('jwt_token');
      if (!token) {
        // Not authenticated yet — retry shortly without counting as a failure.
        reconnectTimer = setTimeout(connect, 3000);
        return;
      }

      const base = env.apiBase.replace(/^http/, 'ws');
      const url = `${base}/${env.apiPrefix}/ws?token=${encodeURIComponent(
        token
      )}`;

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        reconnectTimer = setTimeout(connect, 5000);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attempts = 0;
        if (import.meta.env.DEV) {
          console.log('[presence ws] connected');
        }
      };
      ws.onclose = () => {
        if (cancelled) {
          return;
        }
        attempts += 1;
        const delay = Math.min(30_000, 1000 * Math.pow(2, attempts - 1));
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // onclose will follow and schedule the reconnect.
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (wsRef.current && wsRef.current.readyState <= 1) {
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, []);
}
